import Content from './content';
import { LeaderBoardWeeks } from './weeks';

import { useLoader, useTranslation } from '@CS/hooks';
import { useState, useCallback, useEffect } from 'react';
import { type PopupProps, Popup } from '@CS/components/Popups';

import {
  LeaderboardContainer,
  LeaderboardTitle,
  LeaderboardScores,
  ScoreContainer,
  UserPosition,
  UserScoreList,
  UserName,
  UserScore,
  UserSeparator,
  LeaderboardPage
} from './Leaderboard.styles';

type HighScores = readonly {
  name: string;
  score: number;
}[];

type UserHighScore = HighScores[number] & {
  position: number;
};

export type WeekScores = (week: number) => Promise<{
  userData: UserHighScore;
  weekScores: HighScores;
}>;

type LeaderboardProps = PopupProps & {
  getWeekScores: WeekScores;
  duplicateUser?: boolean;
  currentWeek?: number;
  popup?: boolean;
  weeks?: boolean;
};

export const Leaderboard = ({
  duplicateUser = true,
  currentWeek = 1,
  getWeekScores,
  popup = true,
  weeks = true,
  ...props
}: LeaderboardProps) => {
  const title = useTranslation(Content.title);
  const [loading, setLoading] = useState(true);

  const loader = useLoader(loading, { overlay: true });
  const [scores, setScores] = useState<HighScores>([]);
  const [userData, setUserData] = useState<UserHighScore>();

  const onWeekClick = (week: number) => setSelectedWeek(week);
  const [visibleUserScore, setVisibleUserScore] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(currentWeek - 1);

  const updateSelectedWeek = useCallback(async () => {
    const { userData, weekScores } = await getWeekScores(selectedWeek);

    setVisibleUserScore(
      !!userData && !duplicateUser
        ? userData?.position >= weekScores.length
        : !!userData
    );

    if (!userData.name || userData.position < 0) {
      setVisibleUserScore(false);
    }

    setUserData(userData);
    setScores(weekScores);
  }, [selectedWeek]);

  useEffect(() => {
    setLoading(true);
    updateSelectedWeek().finally(() => setLoading(false));
  }, [selectedWeek]);

  const leaderBoard = (
    <LeaderboardContainer popup={popup}>
      <LeaderboardTitle>{title}</LeaderboardTitle>

      {weeks && (
        <LeaderBoardWeeks
          current={selectedWeek}
          onClick={onWeekClick}
          weeks={currentWeek}
        />
      )}

      <LeaderboardScores>
        {scores.map((user, u) => (
          <ScoreContainer key={u}>
            <UserPosition>{`${u + 1}.`}</UserPosition>

            <UserScoreList>
              <UserName>{user.name}</UserName>
              <UserScore>{user.score}</UserScore>
            </UserScoreList>
          </ScoreContainer>
        ))}

        {userData && visibleUserScore && (
          <>
            <UserSeparator />

            <ScoreContainer user={!!userData}>
              <UserPosition>{`${userData.position}.`}</UserPosition>

              <UserScoreList>
                <UserName>{userData.name}</UserName>
                <UserScore>{userData.score}</UserScore>
              </UserScoreList>
            </ScoreContainer>
          </>
        )}
      </LeaderboardScores>

      {loader}
    </LeaderboardContainer>
  );

  return popup ? (
    <Popup width="90" {...props}>
      {leaderBoard}
    </Popup>
  ) : (
    <LeaderboardPage>{leaderBoard}</LeaderboardPage>
  );
};
